import { useMutation } from '@apollo/client/react';
import { useState, type ReactNode } from 'react';

import { Button } from '@/components/Button/Button';
import { Dialog } from '@/components/Dialog/Dialog';
import { DELETE_JOB, isInFlight, JOBS, jobTitle, type Job } from '@/papermill/api';
import { useToast } from '@/toast/useToast';

export interface DeleteJob {
  requestDelete: (job: Pick<Job, 'id' | 'filename' | 'status' | 'kind'>) => void;
  dialog: ReactNode;
}

/** Asks before deleting a job, deletes it, and says how it went. */
export function useDeleteJob(onDeleted?: (id: string) => void): DeleteJob {
  const toast = useToast();
  const [pending, setPending] = useState<Pick<Job, 'id' | 'filename' | 'status' | 'kind'> | null>(
    null,
  );
  const [deleteJob, deleting] = useMutation(DELETE_JOB, { refetchQueries: [JOBS] });

  async function confirm(job: Pick<Job, 'id'>) {
    try {
      await deleteJob({ variables: { id: job.id } });
      setPending(null);
      onDeleted?.(job.id);
    } catch {
      setPending(null);
      toast.error({ title: 'Could not delete the job', description: 'Try again in a moment.' });
    }
  }

  const dialog = pending ? (
    <Dialog
      title={`Delete “${jobTitle(pending)}”?`}
      description={
        isInFlight(pending)
          ? 'The job stops being waited on, the upload and anything it produced are deleted, and you can start another job straight away.'
          : pending.kind === 'audio' || pending.kind === 'live'
            ? 'The recording, the transcript and the extraction are deleted. This cannot be undone.'
            : 'The upload, the cleaned PDF and the extraction are deleted. This cannot be undone.'
      }
      onClose={() => setPending(null)}
      dismissable={!deleting.loading}
      actions={
        <>
          <Button variant="ghost" onClick={() => setPending(null)} disabled={deleting.loading}>
            Cancel
          </Button>
          <Button variant="danger" loading={deleting.loading} onClick={() => void confirm(pending)}>
            Delete job
          </Button>
        </>
      }
    />
  ) : null;

  return { requestDelete: setPending, dialog };
}
